import React, { useState, useMemo } from 'react'
import { makeStyles, Tooltip } from '@material-ui/core'
import Icon from '../../../../../../atoms/Icon'
import { useAgentPanelContext } from '../AgentPanelContext'

const useStyles = makeStyles(() => ({
  wrapper: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    marginLeft: 'auto'
  },
  infoIcon: {
    cursor: 'pointer',
    display: 'inline-flex',
    alignItems: 'center',
    opacity: 0.5,
    '&:hover': {
      opacity: 1
    }
  },
  errorIcon: {
    display: 'inline-flex',
    alignItems: 'center',
    cursor: 'help'
  },
  tooltipContent: {
    display: 'flex',
    flexDirection: 'column',
    gap: '6px',
    padding: '4px 0',
    fontSize: '11px'
  },
  tooltipRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px'
  },
  tooltipLabel: {
    color: 'var(--color-neutral-400)',
    fontWeight: 600,
    textTransform: 'uppercase',
    fontSize: '9px',
    letterSpacing: '0.5px'
  },
  threadIdText: {
    fontFamily: 'var(--font-family-monospace, monospace)',
    cursor: 'pointer',
    '&:hover': {
      textDecoration: 'underline'
    }
  },
  tokenRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    fontFamily: 'var(--font-family-monospace, monospace)',
    fontSize: '10px'
  },
  modelName: {
    opacity: 0.7
  }
}))

function formatTokens (count) {
  if (count >= 1000000) return `${(count / 1000000).toFixed(1)}M`
  if (count >= 1000) return `${(count / 1000).toFixed(1)}k`
  return String(count)
}

function ThreadMetadata () {
  const classes = useStyles()
  const { threadId, threadData, errorMessage } = useAgentPanelContext()
  const [copied, setCopied] = useState(false)

  const usageItems = useMemo(() => {
    const usage = threadData?.usage
    if (!usage || typeof usage !== 'object') return []

    const items = []
    for (const [model, tokens] of Object.entries(usage)) {
      const input = tokens?.inputTokens || 0
      const output = tokens?.outputTokens || 0
      if (input > 0 || output > 0) {
        items.push({ model, input, output })
      }
    }
    return items
  }, [threadData?.usage])

  const handleCopyThreadId = async (e) => {
    e.stopPropagation()
    if (!threadId) return
    try {
      await navigator.clipboard.writeText(threadId)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch { /* clipboard not available */ }
  }

  if (!threadId) return null

  const tooltipBody = (
    <div className={classes.tooltipContent}>
      <div>
        <div className={classes.tooltipLabel}>Thread ID</div>
        <div className={classes.threadIdText} onClick={handleCopyThreadId}>
          {copied ? 'Copied!' : threadId}
        </div>
      </div>
      {usageItems.length > 0 && (
        <div>
          <div className={classes.tooltipLabel}>Token Usage</div>
          {usageItems.map(({ model, input, output }) => (
            <div key={model} className={classes.tokenRow}>
              <span className={classes.modelName}>{model}</span>
              <span>⬆ {formatTokens(input)}</span>
              <span>⬇ {formatTokens(output)}</span>
            </div>
          ))}
        </div>
      )}
      {threadData?.status && (
        <div>
          <div className={classes.tooltipLabel}>Status</div>
          <div>{threadData.status}</div>
        </div>
      )}
    </div>
  )

  return (
    <div className={classes.wrapper}>
      <Tooltip title={tooltipBody} placement='bottom-end' arrow interactive>
        <span className={classes.infoIcon}>
          <Icon name='info' customSize='14px' />
        </span>
      </Tooltip>
      {errorMessage && (
        <Tooltip title={errorMessage} placement='bottom' arrow>
          <span className={classes.errorIcon}>
            <Icon name='warning' customSize='14px' style={{ color: 'var(--color-danger-500)' }} />
          </span>
        </Tooltip>
      )}
    </div>
  )
}

export default ThreadMetadata
